import { useState, useEffect, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { useAuthStore } from '@/store/authStore';

interface TableInfo {
  name: string;
  row_count: number;
  columns: Array<{
    name: string;
    type: string;
    nullable: boolean;
    default: string | null;
  }>;
}

interface TablePreview {
  table_name: string;
  columns: string[];
  rows: Array<Record<string, unknown>>;
  total_count: number;
}

interface QueryResult {
  success: boolean;
  columns: string[];
  rows: Array<Record<string, unknown>>;
  row_count: number;
  duration_ms: number;
  error?: string;
}

interface SystemStatus {
  database: string;
  stripe: string;
  perplexity: string;
  firebase: string;
  environment: string;
}

const API_URL = import.meta.env.VITE_API_URL || 'https://republic-atlas-api-946143380554.us-central1.run.app';

export const SQLExplorer = () => {
  const { isAuthenticated } = useAuthStore();
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [selectedTable, setSelectedTable] = useState<TableInfo | null>(null);
  const [tablePreview, setTablePreview] = useState<TablePreview | null>(null);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [customQuery, setCustomQuery] = useState('');
  const [queryResult, setQueryResult] = useState<QueryResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allPreviews, setAllPreviews] = useState<Record<string, TablePreview>>({});

  // Fetch with auth
  const fetchWithAuth = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    
    const token = await user.getIdToken();
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }
    
    return response.json();
  }, []);

  // Load tables on mount
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch tables
        const tablesData = await fetchWithAuth('/api/v1/debug/tables');
        setTables(tablesData);
        
        // Fetch system status
        const statusData = await fetchWithAuth('/api/v1/debug/status');
        setSystemStatus(statusData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [isAuthenticated, fetchWithAuth]);

  // Load preview when table selected
  useEffect(() => {
    if (!selectedTable || !isAuthenticated) {
      setTablePreview(null);
      return;
    }
    
    const loadPreview = async () => {
      try {
        const preview = await fetchWithAuth(`/api/v1/debug/tables/${selectedTable.name}/preview?limit=5`);
        setTablePreview(preview);
      } catch (err) {
        console.error('Failed to load preview:', err);
      }
    };
    
    loadPreview();
  }, [selectedTable, isAuthenticated, fetchWithAuth]);

  // Load all table previews (HEAD 5 for each)
  const loadAllPreviews = async () => {
    setIsLoading(true);
    try {
      const previews = await fetchWithAuth('/api/v1/debug/tables/all-previews?limit=5');
      setAllPreviews(previews);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load previews');
    } finally {
      setIsLoading(false);
    }
  };

  // Execute custom query
  const executeQuery = async () => {
    if (!customQuery.trim()) return;
    
    setIsLoading(true);
    setQueryResult(null);
    
    try {
      const result = await fetchWithAuth('/api/v1/debug/query', {
        method: 'POST',
        body: JSON.stringify({ query: customQuery }),
      });
      setQueryResult(result);
    } catch (err) {
      setQueryResult({
        success: false,
        columns: [],
        rows: [],
        row_count: 0,
        duration_ms: 0,
        error: err instanceof Error ? err.message : 'Query failed',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const totalRows = tables.reduce((sum, t) => sum + t.row_count, 0);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
      case 'configured':
        return 'text-green-400';
      case 'not_configured':
      case 'not_initialized':
      case 'sdk_not_installed':
        return 'text-yellow-400';
      case 'error':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected':
      case 'configured':
        return '✓';
      case 'not_configured':
      case 'not_initialized':
        return '○';
      case 'error':
        return '✗';
      default:
        return '?';
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>🔒 Login required to access SQL Explorer</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* System Status Summary */}
      <div className="bg-gradient-to-r from-cyan-900/50 to-blue-900/50 rounded-lg p-3 border border-cyan-500/30">
        <div className="flex items-center justify-between">
          <p className="text-sm">
            <span className="text-cyan-400 font-medium">Database:</span>{' '}
            <span className="text-gray-200">
              {tables.length} tables, {totalRows.toLocaleString()} rows
            </span>
          </p>
          {systemStatus && (
            <div className="flex items-center gap-3 text-xs">
              <span className={getStatusColor(systemStatus.database)}>
                DB {getStatusIcon(systemStatus.database)}
              </span>
              <span className={getStatusColor(systemStatus.stripe)}>
                Stripe {getStatusIcon(systemStatus.stripe)}
              </span>
              <span className={getStatusColor(systemStatus.perplexity)}>
                Sonar {getStatusIcon(systemStatus.perplexity)}
              </span>
            </div>
          )}
        </div>
        {error && <p className="text-red-400 text-xs mt-1">⚠️ {error}</p>}
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2">
        <button
          onClick={loadAllPreviews}
          disabled={isLoading}
          className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 px-3 py-1.5 rounded text-xs font-medium"
        >
          📊 Load All Tables (HEAD 5)
        </button>
        <button
          onClick={() => {
            setTables([]);
            setAllPreviews({});
            setSelectedTable(null);
          }}
          className="bg-gray-700 hover:bg-gray-600 px-3 py-1.5 rounded text-xs"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Tables Grid */}
      <div className="bg-gray-800 rounded-lg p-3">
        <h4 className="text-sm font-medium text-cyan-400 mb-2">📊 Tables</h4>
        {isLoading && tables.length === 0 ? (
          <div className="text-center py-4 text-gray-500">Loading tables...</div>
        ) : (
          <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
            {tables.map((table) => (
              <div
                key={table.name}
                onClick={() => setSelectedTable(selectedTable?.name === table.name ? null : table)}
                className={`p-2 rounded cursor-pointer text-xs transition-colors ${
                  selectedTable?.name === table.name 
                    ? 'bg-cyan-900/50 border border-cyan-500' 
                    : 'bg-gray-900 hover:bg-gray-850'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-cyan-400 font-mono truncate">{table.name}</span>
                  <span className="text-gray-500">{table.row_count.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected Table Details & Preview */}
      {selectedTable && (
        <div className="bg-gray-800 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-cyan-400">
              🗄️ {selectedTable.name}
            </h4>
            <span className="text-xs text-gray-500">
              {selectedTable.row_count.toLocaleString()} rows
            </span>
          </div>
          
          {/* Columns */}
          <div className="mb-3 space-y-1 max-h-24 overflow-y-auto">
            {selectedTable.columns.map((col) => (
              <div key={col.name} className="flex items-center text-[10px] bg-gray-900 rounded px-2 py-1">
                <span className="text-gray-300 w-28 font-mono truncate">{col.name}</span>
                <span className="text-purple-400 flex-1">{col.type}</span>
                <span className={col.nullable ? 'text-gray-600' : 'text-red-400'}>
                  {col.nullable ? 'NULL' : 'NOT NULL'}
                </span>
              </div>
            ))}
          </div>
          
          {/* Preview Data */}
          {tablePreview && tablePreview.rows.length > 0 && (
            <div>
              <h5 className="text-xs text-gray-400 mb-1">Preview (HEAD 5):</h5>
              <div className="overflow-x-auto">
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="border-b border-gray-700">
                      {tablePreview.columns.slice(0, 5).map(col => (
                        <th key={col} className="text-left p-1 text-gray-500 font-mono">{col}</th>
                      ))}
                      {tablePreview.columns.length > 5 && (
                        <th className="text-gray-600">+{tablePreview.columns.length - 5}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {tablePreview.rows.map((row, i) => (
                      <tr key={i} className="border-b border-gray-800">
                        {tablePreview.columns.slice(0, 5).map(col => (
                          <td key={col} className="p-1 text-gray-300 font-mono truncate max-w-[100px]">
                            {row[col] === null ? <span className="text-gray-600">NULL</span> : String(row[col])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* All Previews View */}
      {Object.keys(allPreviews).length > 0 && (
        <div className="bg-gray-800 rounded-lg p-3">
          <h4 className="text-sm font-medium text-cyan-400 mb-2">📋 All Table Previews</h4>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {Object.entries(allPreviews).map(([tableName, preview]) => (
              <div key={tableName} className="bg-gray-900 rounded p-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-cyan-400 font-mono text-xs">{tableName}</span>
                  <span className="text-gray-500 text-[10px]">{preview.total_count} rows</span>
                </div>
                {preview.rows.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-[10px]">
                      <thead>
                        <tr className="border-b border-gray-700">
                          {preview.columns.slice(0, 4).map(col => (
                            <th key={col} className="text-left p-1 text-gray-500">{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.slice(0, 3).map((row, i) => (
                          <tr key={i}>
                            {preview.columns.slice(0, 4).map(col => (
                              <td key={col} className="p-1 text-gray-400 truncate max-w-[80px]">
                                {row[col] === null ? '-' : String(row[col]).slice(0, 20)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <span className="text-gray-600 text-[10px]">Empty table</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Custom Query */}
      <div className="bg-gray-800 rounded-lg p-3">
        <h4 className="text-sm font-medium text-cyan-400 mb-2">⚡ Custom Query (SELECT only)</h4>
        <textarea
          value={customQuery}
          onChange={(e) => setCustomQuery(e.target.value)}
          placeholder="SELECT * FROM candidates LIMIT 10"
          className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-xs font-mono text-gray-300 h-16 resize-none"
        />
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={executeQuery}
            disabled={isLoading || !customQuery.trim()}
            className="bg-green-600 hover:bg-green-700 disabled:opacity-50 px-3 py-1 rounded text-xs font-medium"
          >
            ▶ Execute
          </button>
          <button
            onClick={() => setCustomQuery('SELECT * FROM candidates LIMIT 5')}
            className="bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded text-[10px]"
          >
            candidates
          </button>
          <button
            onClick={() => setCustomQuery('SELECT * FROM political_offices LIMIT 5')}
            className="bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded text-[10px]"
          >
            offices
          </button>
          <button
            onClick={() => setCustomQuery('SELECT * FROM jurisdictions LIMIT 5')}
            className="bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded text-[10px]"
          >
            jurisdictions
          </button>
        </div>
      </div>

      {/* Query Result */}
      {queryResult && (
        <div className="bg-gray-800 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-cyan-400">📋 Query Result</h4>
            <div className="flex items-center gap-2 text-[10px]">
              <span className={queryResult.success ? 'text-green-400' : 'text-red-400'}>
                {queryResult.success ? '✓' : '✗'} {queryResult.success ? 'Success' : 'Error'}
              </span>
              <span className="text-gray-500">{queryResult.duration_ms}ms</span>
              <span className="text-gray-500">{queryResult.row_count} rows</span>
            </div>
          </div>
          
          {queryResult.error ? (
            <div className="bg-red-900/30 border border-red-500/30 rounded p-2 text-red-400 text-xs">
              {queryResult.error}
            </div>
          ) : queryResult.rows.length > 0 ? (
            <div className="overflow-x-auto max-h-40">
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="border-b border-gray-700">
                    {queryResult.columns.map(col => (
                      <th key={col} className="text-left p-1 text-gray-500 font-mono">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {queryResult.rows.map((row, i) => (
                    <tr key={i} className="border-b border-gray-800">
                      {queryResult.columns.map(col => (
                        <td key={col} className="p-1 text-gray-300 font-mono truncate max-w-[120px]">
                          {row[col] === null ? <span className="text-gray-600">NULL</span> : String(row[col])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-gray-500 text-xs">No rows returned</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SQLExplorer;
